import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  MapPin, Users, Star, Bed, Bath, Wifi, Waves, TreePine, Music2, Flame,
  Car, ChefHat, ArrowLeft, Mountain, Utensils, ShoppingBag, Hospital,
  Sparkles, Calendar, ShieldCheck, Heart, MessageCircle,
  Cake, Briefcase, PartyPopper,
} from "lucide-react";
import InquiryDialog from "../components/InquiryDialog";
import ReviewsCarousel from "../components/ReviewsCarousel";

// Each amenity has: icon, short description, category
const AMENITY_DETAILS = {
  "Pool":            { icon: Waves,    cat: "Outdoor",   desc: "Heated, open year-round. Cabanas + towel service." },
  "Lawn":            { icon: TreePine, cat: "Outdoor",   desc: "Manicured, large enough for ceremonies and cocktail setups." },
  "Garden":          { icon: TreePine, cat: "Outdoor",   desc: "Landscaped herb and flower garden, maintained year-round." },
  "Orchard":         { icon: TreePine, cat: "Outdoor",   desc: "Walk through a heritage mango orchard; seasonal fruit on the trees." },
  "Forest Trail":    { icon: Mountain, cat: "Outdoor",   desc: "Private trail with optional early-morning guided walks." },
  "Riverfront":      { icon: Waves,    cat: "Outdoor",   desc: "Private river-access deck with seating and fire pit." },
  "Bonfire":         { icon: Flame,    cat: "Outdoor",   desc: "Sunken bonfire pit with seating for 20. Firewood included." },
  "BBQ":             { icon: Flame,    cat: "Food & Bar",desc: "Outdoor BBQ pit with live-fire grill station." },
  "Chef on request": { icon: ChefHat,  cat: "Food & Bar",desc: "Curated menus by partner chefs — Indian, Continental, Asian." },
  "Wine Cellar":     { icon: Utensils, cat: "Food & Bar",desc: "Climate-controlled cellar with 60+ curated labels." },
  "Vineyard":        { icon: TreePine, cat: "Outdoor",   desc: "Working vineyard walk-through with the resident winemaker." },
  "Music System":    { icon: Music2,   cat: "Tech",      desc: "Pro-grade speakers, wired across lawn, pool and indoors." },
  "Projector":       { icon: Mountain, cat: "Tech",      desc: "4K outdoor projector + screen, set up on request." },
  "Outdoor Cinema":  { icon: Mountain, cat: "Tech",      desc: "Amphitheatre cinema with bean bags. Snack bar on request." },
  "WiFi":            { icon: Wifi,     cat: "Tech",      desc: "100 Mbps fibre across the property — work-friendly." },
  "AC":              { icon: Wifi,     cat: "Comfort",   desc: "Central air conditioning across all bedrooms and common areas." },
  "Hot Tub":         { icon: Waves,    cat: "Comfort",   desc: "6-seat hot tub with temperature control. Towels included." },
  "Soaking Tub":     { icon: Waves,    cat: "Comfort",   desc: "Deep copper soaking tub in the master suite." },
  "Rooftop":         { icon: Mountain, cat: "Outdoor",   desc: "A wrap-around rooftop with sunset views and lounge seating." },
  "Event Space":     { icon: Users,    cat: "Comfort",   desc: "5,000 sq ft open lawn ideal for ceremonies up to 200 guests." },
  "Parking":         { icon: Car,      cat: "Comfort",   desc: "Valet-style parking for up to 25 cars on-site." },
  "Kayaks":          { icon: Waves,    cat: "Outdoor",   desc: "Two tandem kayaks with life jackets and basic instruction." },
};

const CATEGORY_ORDER = ["Comfort", "Outdoor", "Food & Bar", "Tech"];

const DEFAULT_NEARBY = [
  { name: "Chutneys (restaurant)", dist: "8 km", icon: Utensils },
  { name: "DLF Mall of Hyderabad", dist: "18 km", icon: ShoppingBag },
  { name: "Golconda Fort",         dist: "24 km", icon: Mountain },
  { name: "Apollo Hospital",       dist: "12 km", icon: Hospital },
];

const PERFECT_FOR_ICONS = {
  "Birthdays":        Cake,
  "Weddings":         Heart,
  "Corporate Outings":Briefcase,
  "Bachelor Parties": PartyPopper,
  "Family Events":    Users,
  "Weekend Getaways": TreePine,
};

const INCLUDED = [
  { title: "House staff & housekeeping", body: "Daily housekeeping, kitchen helper and a dedicated property manager during your stay." },
  { title: "Welcome kit",                 body: "Curated welcome basket on arrival — local snacks, fresh fruit, sparkling water." },
  { title: "Linens, towels, toiletries",  body: "Hotel-grade linens, plush towels, premium organic toiletries refreshed daily." },
  { title: "Bonfire setup",               body: "Sunset bonfire setup with firewood, marshmallow station and seating arrangement." },
];

export default function PropertyDetail() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [idx, setIdx] = useState(0);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    api.get(`/properties/${id}`).then((r) => setP(r.data)).catch(() => {});
    window.scrollTo(0, 0);
  }, [id]);

  if (!p) return <div className="py-40 text-center text-[#5B6B63]">Loading…</div>;

  const priceFmt = new Intl.NumberFormat("en-IN").format(p.price_per_night);

  // Group amenities by category
  const grouped = {};
  for (const a of (p.amenities || [])) {
    const d = AMENITY_DETAILS[a] || { icon: TreePine, cat: "Comfort", desc: "Included with your stay." };
    if (!grouped[d.cat]) grouped[d.cat] = [];
    grouped[d.cat].push({ name: a, ...d });
  }

  const openInquiry = () => setOpen(true);

  return (
    <main className="pb-24 bg-[#FAFAF8]" data-testid="property-detail-page">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 pt-8">
        <Link to="/stays" className="inline-flex items-center gap-2 text-sm font-sora font-medium text-[#1F5A43] hover:text-[#0E2E24]" data-testid="back-to-stays">
          <ArrowLeft size={14} /> All stays
        </Link>
      </div>

      {/* Gallery */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 mt-6">
        <div className="grid md:grid-cols-4 md:grid-rows-2 gap-3 md:h-[560px]">
          <div
            className="md:col-span-3 md:row-span-2 relative rounded-2xl overflow-hidden aspect-[4/3] md:aspect-auto cursor-pointer"
            onClick={() => setIdx((idx + 1) % p.images.length)}
            data-testid="gallery-primary"
          >
            <img src={p.images[idx]} alt={p.name} className="img-cover" />
            {p.managed && (
              <Badge className="absolute top-5 left-5 bg-[#F4A930] hover:bg-[#F4A930] text-[#0E2E24] font-sora text-[10px] tracking-widest uppercase rounded-full px-3 py-1">
                Managed by Appy
              </Badge>
            )}
            <div className="absolute bottom-4 right-4 bg-[#0E2E24]/85 backdrop-blur text-[#FAFAF8] rounded-full px-3 py-1.5 flex items-center gap-1 text-xs font-sora">
              {idx + 1} / {p.images.length}
            </div>
          </div>
          {p.images.slice(1, 3).map((img, i) => (
            <div key={i} className="hidden md:block rounded-2xl overflow-hidden">
              <img src={img} alt="" className="img-cover" />
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-12 mt-14 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-8 space-y-12">
          {/* Title block */}
          <header>
            <div className="flex items-center gap-2 text-[#1F5A43] text-sm font-sora">
              <MapPin size={14} /> {p.location}
            </div>
            <h1 className="mt-3 font-sora text-4xl md:text-5xl font-semibold tracking-tight text-[#0E2E24] leading-[1.05]" data-testid="property-title">
              {p.name}
            </h1>
            <p className="mt-4 text-lg text-[#1F5A43] font-sora">{p.short_tagline}</p>

            <div className="mt-6 flex flex-wrap gap-5 text-sm font-sora text-[#0E2E24]/85">
              <span className="flex items-center gap-1.5"><Star size={14} className="fill-[#F4A930] text-[#F4A930]" /> {p.rating.toFixed(2)} ({p.review_count} reviews)</span>
              <span className="flex items-center gap-1.5"><Users size={14} className="text-[#1F5A43]" /> Up to {p.capacity}</span>
              <span className="flex items-center gap-1.5"><Bed size={14} className="text-[#1F5A43]" /> {p.bedrooms} bedrooms</span>
              <span className="flex items-center gap-1.5"><Bath size={14} className="text-[#1F5A43]" /> {p.bathrooms} baths</span>
            </div>
          </header>

          {/* About */}
          <section>
            <div className="kicker mb-3">About this stay</div>
            <p className="text-[#5B6B63] leading-relaxed text-lg">{p.description}</p>
          </section>

          {/* PERFECT FOR strip */}
          {p.occasions?.length > 0 && (
            <section>
              <div className="kicker mb-4">Perfect for</div>
              <div className="flex flex-wrap gap-3">
                {p.occasions.map((o) => {
                  const I = PERFECT_FOR_ICONS[o] || Sparkles;
                  return (
                    <Link key={o} to={`/stays?occasion=${encodeURIComponent(o)}`} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-[#0E2E24]/10 text-sm font-sora font-medium text-[#0E2E24] hover:border-[#1F5A43]/40 hover:bg-[#E8F2EC] transition-colors" data-testid={`perfect-for-${o.toLowerCase().replace(/\s/g, '-')}`}>
                      <I size={14} className="text-[#1F5A43]" /> {o}
                    </Link>
                  );
                })}
              </div>
            </section>
          )}

          {/* AMENITIES — major redesign, grouped + visual */}
          <section data-testid="amenities-section">
            <div className="flex items-end justify-between flex-wrap gap-3 mb-6">
              <div>
                <div className="kicker mb-2">Amenities</div>
                <h2 className="font-sora text-2xl md:text-3xl font-semibold tracking-tight text-[#0E2E24] leading-tight">
                  Everything you'll have on-site.
                </h2>
              </div>
              <span className="text-sm font-sora text-[#5B6B63]">{p.amenities?.length || 0} amenities included</span>
            </div>

            <div className="space-y-6">
              {CATEGORY_ORDER.filter((c) => grouped[c]).map((cat) => (
                <div key={cat}>
                  <div className="flex items-center gap-3 mb-3">
                    <span className="kicker text-[#F4A930]">{cat}</span>
                    <span className="flex-1 h-px bg-[#0E2E24]/10" />
                    <span className="text-xs font-sora text-[#5B6B63]">{grouped[cat].length} items</span>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {grouped[cat].map((a) => (
                      <div key={a.name} className="bg-white border border-[#0E2E24]/10 rounded-2xl p-5 flex gap-4 hover:border-[#7CB342]/40 hover:shadow-[0_10px_30px_-15px_rgba(14,46,36,0.18)] transition-all" data-testid={`amenity-${a.name.toLowerCase().replace(/\s/g, '-')}`}>
                        <div className="h-11 w-11 rounded-xl bg-[#E8F2EC] flex items-center justify-center shrink-0">
                          <a.icon size={18} className="text-[#1F5A43]" />
                        </div>
                        <div>
                          <div className="font-sora text-sm font-semibold text-[#0E2E24]">{a.name}</div>
                          <p className="mt-1 text-sm text-[#5B6B63] leading-relaxed">{a.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* WHAT'S INCLUDED */}
          <section>
            <div className="kicker mb-3">What's included with every stay</div>
            <h2 className="font-sora text-2xl md:text-3xl font-semibold tracking-tight text-[#0E2E24] leading-tight">
              The little things, already handled.
            </h2>
            <div className="mt-6 grid md:grid-cols-2 gap-4">
              {INCLUDED.map((x) => (
                <div key={x.title} className="bg-white border border-[#0E2E24]/10 rounded-2xl p-5 flex gap-4">
                  <div className="h-9 w-9 rounded-xl bg-[#F4A930]/20 flex items-center justify-center shrink-0">
                    <ShieldCheck size={16} className="text-[#1F5A43]" />
                  </div>
                  <div>
                    <h3 className="font-sora text-sm font-semibold text-[#0E2E24]">{x.title}</h3>
                    <p className="mt-1 text-sm text-[#5B6B63] leading-relaxed">{x.body}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* RULES */}
          <section>
            <div className="kicker mb-4">House rules</div>
            <ul className="grid md:grid-cols-2 gap-3">
              {p.rules.map((r) => (
                <li key={r} className="flex items-start gap-3 bg-white border border-[#0E2E24]/10 rounded-2xl p-4 font-sora text-sm text-[#0E2E24]/85">
                  <span className="mt-1 h-2 w-2 rounded-full bg-[#F4A930] shrink-0" /> {r}
                </li>
              ))}
            </ul>
          </section>

          {/* NEARBY */}
          <section>
            <div className="kicker mb-4">Nearby places</div>
            <div className="grid sm:grid-cols-2 gap-3">
              {DEFAULT_NEARBY.map((n) => (
                <div key={n.name} className="flex items-center justify-between bg-white border border-[#0E2E24]/10 rounded-2xl p-5" data-testid={`nearby-${n.name.toLowerCase().split(' ')[0]}`}>
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-xl bg-[#E8F2EC] flex items-center justify-center">
                      <n.icon size={17} className="text-[#1F5A43]" />
                    </div>
                    <div className="font-sora text-sm font-medium text-[#0E2E24]">{n.name}</div>
                  </div>
                  <span className="font-sora text-sm text-[#5B6B63]">{n.dist}</span>
                </div>
              ))}
            </div>
          </section>

          {/* MAP placeholder */}
          <section>
            <div className="kicker mb-4">Neighbourhood</div>
            <div className="rounded-2xl overflow-hidden bg-[#E8F2EC] aspect-[16/7] flex items-center justify-center border border-[#0E2E24]/10">
              <div className="text-center">
                <MapPin size={30} className="text-[#1F5A43] mx-auto" />
                <div className="mt-3 font-sora text-xl font-semibold text-[#0E2E24]">{p.location}</div>
                <div className="mt-1 text-sm text-[#5B6B63]">Detailed address shared after booking confirmation</div>
              </div>
            </div>
          </section>

          {/* REVIEWS */}
          <ReviewsCarousel propertyId={p.id} propertyRating={p.rating} propertyReviewCount={p.review_count} />
        </div>

        {/* Sticky Book Now */}
        <aside className="lg:col-span-4">
          <div className="lg:sticky lg:top-24 space-y-4">
            <div className="bg-white border border-[#0E2E24]/10 rounded-2xl p-7">
              <div className="flex items-baseline gap-2">
                <span className="font-sora text-3xl font-semibold text-[#0E2E24]">₹{priceFmt}</span>
                <span className="text-sm text-[#5B6B63] font-sora">/ night</span>
              </div>
              <div className="mt-1 flex items-center gap-1 text-sm text-[#1F5A43]">
                <Star size={13} className="fill-[#F4A930] text-[#F4A930]" /> {p.rating.toFixed(2)} · {p.review_count} reviews
              </div>

              <Button
                onClick={openInquiry}
                className="mt-7 w-full bg-[#0E2E24] hover:bg-[#1F5A43] text-[#FAFAF8] rounded-full py-6 font-sora font-medium gap-2"
                data-testid="property-book-now"
              >
                <MessageCircle size={16} /> Enquire & Book
              </Button>
              <Button
                onClick={openInquiry}
                variant="outline"
                className="mt-2 w-full bg-white border-[#0E2E24]/15 text-[#0E2E24] hover:bg-[#E8F2EC] rounded-full py-6 font-sora font-medium gap-2"
                data-testid="property-check-availability"
              >
                <Calendar size={15} /> Check availability
              </Button>

              <div className="mt-5 pt-5 border-t border-[#0E2E24]/10 space-y-2 text-sm font-sora text-[#5B6B63]">
                <div className="flex justify-between"><span>Host desk response</span><span className="text-[#0E2E24] font-medium">~30 min</span></div>
                <div className="flex justify-between"><span>Security deposit</span><span className="text-[#0E2E24] font-medium">Refundable</span></div>
                <div className="flex justify-between"><span>Cancellation</span><span className="text-[#0E2E24] font-medium">Flexible</span></div>
              </div>

              <p className="mt-5 text-xs text-[#5B6B63]">
                You won't be charged yet. Our host desk will call you to finalise dates & a quote.
              </p>
            </div>

            {/* Quick trust */}
            <div className="bg-[#E8F2EC] border border-[#0E2E24]/10 rounded-2xl p-5">
              <div className="flex items-center gap-2 text-sm font-sora font-semibold text-[#0E2E24]">
                <ShieldCheck size={16} className="text-[#7CB342]" /> Verified by Appy Stays
              </div>
              <p className="mt-2 text-xs text-[#5B6B63] leading-relaxed">
                Property visited, photographed and quality-tested by our team before going live.
              </p>
            </div>
          </div>
        </aside>
      </section>

      <InquiryDialog open={open} onOpenChange={setOpen} property={p} defaultValues={{ guests: p.capacity, occasion: p.occasions?.[0] || "Weekend Getaways" }} />
    </main>
  );
}
